/* "Machine taken": which substitutes to offer for a plan exercise. Mo's own
 * history comes first: swaps[date][exerciseId] = name, with the id resolved
 * to its name through every plan day (the same exercise has other ids on A/B
 * days, and ids change after a plan reset) and names folded through
 * exerciseAliases, weighted 0.97^daysAgo so old habits fade. Then same-muscle
 * library alternatives (logic/muscles). Pure; dates are YYYY-MM-DD. */
import { canonicalName, resolveMuscles, sameMuscleAlternatives } from './logic/muscles';
import { daysBetweenKeys } from './workout';

export const SWAP_DECAY = 0.97;
export const NUDGE_MIN_USES = 6; // "make it the plan exercise?" after 6 swaps ...
export const NUDGE_DAYS = 30; // ... within 30 days
export const MAX_ALTS = 3;

const isObj = v => !!v && typeof v === 'object' && !Array.isArray(v);
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

function planNames(plan) {
  const byId = new Map();
  (Array.isArray(plan) ? plan : []).forEach(day => (Array.isArray(day?.exercises) ? day.exercises : []).forEach(ex => {
    if (ex && typeof ex.id === 'string' && typeof ex.name === 'string') byId.set(ex.id, ex.name);
  }));
  return byId;
}

/**
 * Past swaps away from an exercise, most habitual first.
 * @param {object} state reads plan, swaps, exerciseAliases
 * @param {string} originalName the plan exercise's name
 * @param {string} today YYYY-MM-DD; later dates are ignored
 * @returns {Array<{name: string, count: number, recent: number, score: number, lastDate: string}>}
 *   count = all uses, recent = uses in the last 30 days (today included),
 *   score = sum of 0.97^daysAgo (rounded to 0.001)
 */
export function usualSwaps(state, originalName, today) {
  const aliases = state?.exerciseAliases;
  const target = canonicalName(originalName, aliases);
  const idToName = planNames(state?.plan);
  const byName = new Map();
  Object.keys(isObj(state?.swaps) ? state.swaps : {}).forEach(date => {
    const day = state.swaps[date];
    const ago = daysBetweenKeys(date, today);
    if (!isObj(day) || ago === null || ago < 0) return;
    Object.keys(day).forEach(id => {
      const from = idToName.get(id);
      const to = typeof day[id] === 'string' ? day[id].trim() : '';
      if (!from || !to || canonicalName(from, aliases) !== target) return;
      const name = canonicalName(to, aliases);
      if (name === target) return;
      const e = byName.get(name) || { name, count: 0, recent: 0, score: 0, lastDate: date };
      e.count += 1;
      e.score += SWAP_DECAY ** ago;
      if (ago < NUDGE_DAYS) e.recent += 1;
      if (date > e.lastDate) e.lastDate = date;
      byName.set(name, e);
    });
  });
  return [...byName.values()]
    .map(e => ({ ...e, score: Math.round(e.score * 1000) / 1000 }))
    .sort((a, b) => b.score - a.score || b.count - a.count || cmp(a.name, b.name));
}

/**
 * Sessions per exercise name in the `days` days up to today (dates with at
 * least one done set), for ranking same-muscle alternatives by use.
 * @returns {Object<string, number>} names folded through `aliases`
 */
export function recentSessionCounts(logs, today, days = 60, aliases) {
  const out = {};
  Object.keys(isObj(logs) ? logs : {}).forEach(date => {
    const ago = daysBetweenKeys(date, today);
    const day = logs[date];
    if (ago === null || ago < 0 || ago >= days || !isObj(day)) return;
    Object.keys(day).forEach(name => {
      if (!Array.isArray(day[name]) || !day[name].some(s => s?.done)) return;
      const n = canonicalName(name, aliases);
      out[n] = (out[n] || 0) + 1;
    });
  });
  return out;
}

/**
 * What the swap sheet lists for a plan exercise: up to 3 usual swaps (the
 * plan's agreed `alts` first, then history) and up to 3 same-muscle
 * alternatives. Neither list holds the plan exercise or what is done now;
 * same-muscle leaves out the day's other exercises too. A library exercise
 * Mo already logs under his own name (e.g. 'Barbell Row' for 'Barbell Row
 * (overhand)') is offered under that name, so a swap never splits history.
 * @param {object} state
 * @param {{originalName: string, currentName: string, alts?: string[], todayNames?: string[]}} ex
 * @param {string} today
 * @returns {{usual: Array<{name, count, plan: boolean}>, sameMuscle: Array<{name, muscle, equipment}>}}
 */
export function swapOptions(state, ex, today) {
  const aliases = state?.exerciseAliases;
  const meta = state?.exerciseMeta;
  const history = usualSwaps(state, ex.originalName, today);
  const taken = new Set([ex.originalName, ex.currentName].map(n => canonicalName(n, aliases)));
  const usual = [];
  const add = (raw, plan) => {
    const name = typeof raw === 'string' ? canonicalName(raw.trim(), aliases) : '';
    if (!name || taken.has(name) || usual.length >= MAX_ALTS) return;
    taken.add(name);
    usual.push({ name, count: history.find(h => h.name === name)?.count || 0, plan });
  };
  (Array.isArray(ex.alts) ? ex.alts : []).forEach(n => add(n, true));
  history.forEach(h => add(h.name, false));

  // Logged names by the library entry they stand for: counts add up per
  // entry, and the most used logged name stands in for the entry.
  const matchOf = n => resolveMuscles(n, meta)?.match || n;
  const recent = recentSessionCounts(state?.logs, today, 60, aliases);
  const counts = {};
  Object.keys(recent).forEach(n => { const m = matchOf(n); counts[m] = (counts[m] || 0) + recent[n]; });
  const ownName = new Map();
  const allTime = recentSessionCounts(state?.logs, today, Infinity, aliases);
  Object.keys(allTime).sort((a, b) => allTime[b] - allTime[a] || cmp(a, b)).forEach(n => {
    const m = matchOf(n);
    if (!ownName.has(m)) ownName.set(m, n);
  });
  const skip = [...taken, ...(Array.isArray(ex.todayNames) ? ex.todayNames : [])];
  const sameMuscle = sameMuscleAlternatives(ex.originalName, {
    exclude: [...skip, ...skip.map(matchOf)], limit: MAX_ALTS, exerciseMeta: meta, similar: true, counts,
  }).map(o => ({ ...o, name: ownName.get(o.name) || o.name }));
  return { usual, sameMuscle };
}

/**
 * The substitute Mo uses so often it may as well be the plan exercise: the
 * most used swap with >= 6 uses in the last 30 days, or null.
 * @returns {{name: string, recent: number}|null}
 */
export function swapNudge(state, originalName, today) {
  const top = usualSwaps(state, originalName, today)
    .filter(u => u.recent >= NUDGE_MIN_USES)
    .sort((a, b) => b.recent - a.recent || b.score - a.score)[0];
  return top ? { name: top.name, recent: top.recent } : null;
}

/**
 * Make `newName` the plan exercise of slot `exId`: the old name becomes its
 * first alternative (so it is one tap away when the new machine is taken)
 * and the day's swap to `newName`, now redundant, is removed.
 * @returns {object} new state (unchanged when the slot is missing)
 */
export function promoteSwap(state, exId, newName, dateKey) {
  const name = (newName || '').trim();
  let found = false;
  const plan = (state.plan || []).map(day => {
    if (!day.exercises?.some(e => e.id === exId)) return day;
    return {
      ...day,
      exercises: day.exercises.map(e => {
        if (e.id !== exId || !name || e.name === name) return e;
        found = true;
        const alts = [e.name, ...(e.alts || [])].filter((n, i, all) => n !== name && all.indexOf(n) === i);
        return { ...e, name, alts: alts.slice(0, MAX_ALTS) };
      }),
    };
  });
  if (!found) return state;
  const next = { ...state, plan };
  const day = state.swaps?.[dateKey];
  if (isObj(day) && day[exId] === name) {
    const { [exId]: _gone, ...rest } = day;
    const swaps = { ...state.swaps };
    if (Object.keys(rest).length) swaps[dateKey] = rest;
    else delete swaps[dateKey];
    next.swaps = swaps;
  }
  return next;
}
